import React, { useEffect, useState } from 'react';
import { apiConstants, getGeneric } from '../../common/constants';
import { convertToCSV, downloadCSV, formatDate, parseDateAndTime } from '../../helpers/UtilityHelper';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { Button, Card, CardContent, CardHeader, Chip, Grid, Typography } from '@mui/material';
import Loader from '../../common/Loader';
import { MerchantFilterCached } from '../../common/ProshipFormComponents';
import { FormProvider, RHFDateRangePickerField } from '../../common/hook-form';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { Download } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import CopyIcon from '../../common/CopyIcon';

const headers = [
    { label: "AWB Number", key: "awbNumber" },
    { label: "Credit Amount", key: "credit" },
    { label: 'Debit Amount', key: 'debit' },
    // Weight v2 hold model: HOLD / RELEASE rows move no money and carry the reserved amount here.
    { label: 'Hold Event', key: 'holdEvent' },
    { label: 'Hold Amount', key: 'holdAmount' },
    { label: 'Description', key: 'description' },
    { label: 'Ref. Number', key: 'orderReferenceNumber' },
    { label: 'Trans. Time', key: 'date' }
];

/** HOLD rows reserve, RELEASE rows un-reserve, a DEBIT with a holdEvent is a hold turned into the charge. */
const HOLD_LABEL = { HOLD: 'Hold placed', RELEASE: 'Hold released', CONVERT: 'Hold debited', CONVERTED: 'Hold debited' };

const OrderTransaction = () => {
    const [walletHistory, setWalletHistory] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [loading, setLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar();

    const methods = useForm({
        defaultValues: {
            merchant: "",
            date_range: {
                fromDate: '',
                toDate: ''
            }
        }
    });
    const { handleSubmit, reset, getValues } = methods;


    const loadOrderTransaction = () => {
        const { merchant, fromDate, toDate } = getValues();
        if (merchant) {
            setLoading(true);
            getGeneric(
                `${apiConstants.ORDER_TRANSACTION}?from_date=${formatDate(fromDate, "yyyy-MM-dd")}&to_date=${formatDate(toDate, "yyyy-MM-dd")}&merchantId=${merchant}&limit=${sizePerPage}&offset=${page * sizePerPage}`
            )
                .then(response => {
                    setWalletHistory(response?.data || []);
                })
                .catch(err => {
                    enqueueSnackbar('Failed to load transactions. Please try again.', { variant: 'error' });
                }).finally(() => {
                    setLoading(false);
                });
        }
    }


    useEffect(() => {
        loadOrderTransaction();
    }, [page, sizePerPage]);

    const columns = [
        {
            field: "awbNumber",
            headerAlign: "center",
            hideSortIcons: true,
            flex: 1,
            headerName: "AWB Number",
            width: 160,
            renderCell: (params) => {
                const { row } = params;
                return <Grid container alignItems="center" wrap="nowrap"><Grid item>{row.awbNumber}</Grid><Grid item><CopyIcon text={row.awbNumber} /></Grid></Grid>;
            },
        },
        {
            field: "orderReferenceNumber",
            headerAlign: "center",
            hideSortIcons: true,
            flex: 1,
            headerName: "Reference Number",
        },
        {
            field: "transactionType",
            headerAlign: "center",
            hideSortIcons: true,
            flex: 1,
            headerName: "Transaction Type",
            renderCell: (params) => {
                const { row } = params;
                const moneyMoved = (row.credit && row.credit > 0) || (row.debit && row.debit > 0);
                // A hold row moves no money: label it by its hold event instead of as a zero debit.
                const transactionType = !moneyMoved && row.holdEvent
                    ? (HOLD_LABEL[row.holdEvent] || row.holdEvent)
                    : (row.credit && row.credit > 0 ? 'Credit' : 'Debit');
                const color = !moneyMoved && row.holdEvent ? "warning" : (row.credit && row.credit > 0 ? "success" : "error");
                return (
                    <>
                        <Grid container rowSpacing={2}>
                            <Grid item xs={12}
                            >
                                <Chip size="small" color={color} label={transactionType} sx={{
                                    color: 'white',
                                    height: 25,
                                    margin: 0.5

                                }} />
                            </Grid>
                        </Grid>
                    </>
                );
            },
        },
        {
            field: "transactionAmount",
            headerAlign: "center",
            hideSortIcons: true,
            flex: 1,
            headerName: "Transaction Amount",
            renderCell: (params) => {
                const { row } = params;
                // A hold or release row moves no money, so it has no debit/credit: its amount is holdAmount.
                if (row.holdEvent && !(row.credit > 0) && !(row.debit > 0)) {
                    return row.holdAmount != null ? `Rs.${Number(row.holdAmount).toFixed(2)} (hold)` : '—';
                }
                const transactionAmount = row.credit && row.credit > 0 ? row.credit : row.debit;
                return transactionAmount != null ? 'Rs.' + transactionAmount : '—';
            },
        },
        {
            field: "holdEvent",
            headerAlign: "center",
            hideSortIcons: true,
            flex: 1,
            headerName: "Wallet Hold",
            renderCell: (params) => {
                const { row } = params;
                if (!row.holdEvent) return '—';
                const label = HOLD_LABEL[row.holdEvent] || row.holdEvent;
                return (
                    <Chip size="small" variant="outlined" color="warning"
                        label={row.holdAmount != null ? `${label} · Rs.${row.holdAmount}` : label}
                        sx={{ height: 25, margin: 0.5 }} />
                );
            },
        },
        {
            field: "description",
            headerAlign: "center",
            hideSortIcons: true,
            flex: 1.5,
            headerName: "Description",
        },
        {
            field: "date",
            headerAlign: "center",
            hideSortIcons: true,
            headerName: "Date",
            flex: 1,
            renderCell: (params) => {
                const { row } = params;
                return parseDateAndTime(row.date);
            },
        },

    ]


    const onSubmit = async (data) => {
        if (data.merchant && data.fromDate && data.toDate) {
            loadOrderTransaction()
        } else {
            enqueueSnackbar('Please select merchant and date range to view transactions.', { variant: 'error' });
        }
    };

    const downloadTransactionFile = () => {
        const { merchant, fromDate, toDate } = getValues();
        if (merchant && fromDate && toDate) {
            setBtnLoading(true)
            getGeneric(
                `${apiConstants.ORDER_TRANSACTION}?from_date=${formatDate(fromDate, "yyyy-MM-dd")}&to_date=${formatDate(toDate, "yyyy-MM-dd")}&merchantId=${merchant}&limit=100000`
            ).then(response => {
                setBtnLoading(false);
                const csvData = convertToCSV((response.data && response.data.length > 0) ? response.data : [], headers);
                const filename = 'order-transaction.csv';
                downloadCSV(csvData, filename);
            }).catch(err => {
                console.error(err);
                setBtnLoading(false);
            });
        }
    }


    return (
        <>
            <Card>
                <CardHeader
                    subheader={
                        <Grid item xs={12}>
                            <Typography variant='caption' color={'error'} sx={{ fontWeight: 'bold' }}>Please select merchant and date range to view transactions.</Typography>
                        </Grid>
                    }
                    action={
                        <LoadingButton size='small' startIcon={<Download />} loading={btnLoading} onClick={() => { downloadTransactionFile() }} variant="contained" color="warning"
                            disabled={!walletHistory?.length > 0}
                        >Download</LoadingButton>
                    }
                />
                <CardContent>
                    <FormProvider
                        methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={3} style={{ overflow: 'hidden' }}>
                                        <MerchantFilterCached />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <RHFDateRangePickerField name="date_range" label="Date Range" maxDate={new Date()} />
                                    </Grid>
                                    <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button type="submit" variant="contained" color="primary" size='small'>Search</Button>
                                        <Button sx={{ ml: 1 }} type="button" variant="contained" color="error" size='small' onClick={() => {
                                            reset({
                                                merchant: '',
                                                date_range: {
                                                    fromDate: '',
                                                    toDate: ''
                                                }
                                            })
                                            setWalletHistory([])
                                        }}>Reset</Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
            <Grid item container spacing={2} sx={{ mt: 2 }}>
                {(loading) && <Loader />}
                <Grid item xs={12}>
                    <ProzoDataGrid columns={columns}
                        autoHeight={true}
                        rows={walletHistory?.map((row, index) => ({ ...row, id: index }))}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        pagination={true}
                        hideFooterPagination={false}
                        // filterMode={"server"}
                        paginationMode={'server'}
                        rowHeight={180}
                        rowCount={Number.MAX_VALUE}
                        getRowId={(row) => row.id}
                    />
                    {/* )} */}
                </Grid>
            </Grid>
        </>

    );
}

export default OrderTransaction;